import { Dispatch } from "redux";
import axios from "axios";
import { Message, Participant, User } from "@twilio/conversations";

import { ActionType } from "../action-types";
import {
  Action,
  SetTemplatesAction,
  SetTemplateSendPendingAction,
} from "../actions";
import { ReduxMessage } from "../reducers/messageListReducer";
import { http } from "../../utils/http";
import { TemplateDto } from "../../dto/template";
import { templates } from "../../mocks/mock";
import { USE_MOCKS_DATA } from "../../utils/localUtils";
import { AppThunk } from "./types";

export const upsertMessages = (
  channelSid: string,
  messages: (Message | ReduxMessage)[]
) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.ADD_MESSAGES,
      payload: { channelSid, messages },
    });
  };
};

export const pushMessages = (channelSid: string, messages: Message[]) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.PUSH_MESSAGES,
      payload: { channelSid, messages },
    });
  };
};

export const removeMessages = (channelSid: string, messages: Message[]) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.REMOVE_MESSAGES,
      payload: { channelSid, messages },
    });
  };
};

export const addAttachment = (
  channelSid: string,
  messageSid: string,
  mediaSid: string,
  attachment: Blob
) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.ADD_ATTACHMENT,
      payload: { channelSid, messageSid, mediaSid, attachment },
    });
  };
};

export const clearAttachments = (channelSid: string, messageSid: string) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.CLEAR_ATTACHMENTS,
      payload: { channelSid, messageSid },
    });
  };
};

export const startTyping = (channelSid: string, participant: string) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.TYPING_STARTED,
      payload: { channelSid, participant },
    });
  };
};

export const endTyping = (channelSid: string, participant: string) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.TYPING_ENDED,
      payload: { channelSid, participant },
    });
  };
};

export const updateParticipants = (
  participants: Participant[],
  sid: string
) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.UPDATE_PARTICIPANTS,
      payload: { participants, sid },
    });
  };
};

export const updateUser = (user: User) => {
  return (dispatch: Dispatch<Action>): void => {
    dispatch({
      type: ActionType.UPDATE_USER,
      payload: user,
    });
  };
};

export const setTemplates = (templates: TemplateDto[]): SetTemplatesAction => ({
  type: ActionType.SET_TEMPLATES,
  payload: templates,
});

export const setTemplateSendPending = (
  sid: string,
  pending: boolean
): SetTemplateSendPendingAction => ({
  type: ActionType.TEMPLATE_SEND_PENDING_SET,
  payload: { sid, pending },
});

export const fetchTemplates = (): AppThunk<Promise<TemplateDto[]>> => {
  return async (dispatch) => {
    try {
      const res = await http.get("/api/Templates");
      const items = res.data ?? [];

      if (USE_MOCKS_DATA) {
        dispatch(setTemplates(templates));
      } else {
        dispatch(setTemplates(items));
      }

      return items;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "NO_STATUS";
        const text =
          typeof error.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error.response?.data ?? "");
        throw new Error(`GET /api/Templates failed REQUEST: ${status} ${text}`);
      }
      throw error;
    }
  };
};

export const sendTemplateToConversation =
  (
    conversationSid: string,
    conversationId: string,
    templateId: string,
    variables: Record<string, string>
  ): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      dispatch(setTemplateSendPending(conversationSid, true));
      await http.post(`/api/Conversations/${conversationId}/send-template`, {
        templateId,
        variables,
      });
      // il reset del pending lo fai quando arriva il messageAdded
    } catch (error) {
      dispatch(setTemplateSendPending(conversationSid, false));
      if (axios.isAxiosError(error)) {
        const status = error.response?.status ?? "NO_STATUS";
        const text =
          typeof error.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error.response?.data ?? "");
        throw new Error(
          `POST /api/Conversations/${conversationId}/send-template failed REQUEST: ${status} ${text}`
        );
      }
      throw error;
    }
  };
